import { useMemo, useRef } from "react";

import { useFormContext, useWatch } from "react-hook-form";

import Box from "@/components/Box";
import useAddressFinder from "@/hooks/useAddressFinder";

const PILL_BORDER_COLOR = "#282828";
const PILL_HORIZONTAL_PADDING = "28px";
const PLACEHOLDER_TEXT = "Postcode lookup";

export default function AddressFinderSearch({
  disabled = false,
  shippingCountries = [],
  fieldNames,
  countryValueType = "isoCode",
  updateCountry = true,
  onAddressPopulated,
  label = PLACEHOLDER_TEXT
}) {
  const pillRef = useRef(null);
  const { setValue } = useFormContext();
  const countryFieldName = fieldNames?.country;
  const watchedCountry = useWatch({
    name: countryFieldName,
    disabled: !countryFieldName
  });

  const countryIso2 = useMemo(() => {
    if (countryValueType === "isoCode") {
      return watchedCountry || undefined;
    }

    const match = shippingCountries.find(
      country => String(country.id) === String(watchedCountry)
    );
    return match?.isoCode;
  }, [countryValueType, shippingCountries, watchedCountry]);

  const { inputRef, available } = useAddressFinder({
    disabled,
    shippingCountries,
    countryIso2,
    countryValueType,
    updateCountry,
    fieldNames,
    setValue,
    onAddressPopulated,
    placeholder: label,
    pillRef
  });

  if (!available) {
    return null;
  }

  return (
    <Box
      ref={pillRef}
      data-address-finder-pill
      display="flex"
      width="311px"
      maxWidth="100%"
      minHeight="48px"
      px={PILL_HORIZONTAL_PADDING}
      py="16px"
      justifyContent="space-between"
      alignItems="center"
      borderRadius="100px"
      border="1px solid"
      borderColor={PILL_BORDER_COLOR}
      position="relative"
      css={`
        overflow: visible;

        .idpc_autocomplete {
          position: static !important;
          flex: 1;
          width: 100%;
          min-width: 0;
        }

        input {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          font-family: gilroyMedium, sans-serif;
          font-size: 14px;
          color: ${PILL_BORDER_COLOR};
          padding: 0;
          margin: 0;
        }

        input::placeholder {
          color: #818181;
          opacity: 1;
        }

        @media (min-width: 960px) {
          input {
            font-size: 16px;
          }
        }
      `}
    >
      <Box
        as="input"
        ref={inputRef}
        type="text"
        autoComplete="off"
        disabled={disabled}
        placeholder={label}
        aria-label={label}
        data-testid="address-finder-search"
      />
    </Box>
  );
}
